<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Create Shipment</h3>
              <v-spacer></v-spacer>
              <v-btn class="mx-2 mb-2" fab x-small color="grey" @click="goBack">
                <v-icon dark>mdi-arrow-left</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <!-- Stepper -->
              <v-stepper v-model="currentStep" alt-labels>
                <v-stepper-header>
                  <v-stepper-step
                    :complete="currentStep > 1"
                    step="1"
                    :color="currentStep >= 1 ? 'primary' : 'grey'"
                  >
                    Pickup Information
                  </v-stepper-step>

                  <v-divider></v-divider>

                  <v-stepper-step
                    :complete="currentStep > 2"
                    step="2"
                    :color="currentStep >= 2 ? 'primary' : 'grey'"
                  >
                    Customer Information
                  </v-stepper-step>

                  <v-divider></v-divider>

                  <v-stepper-step
                    :complete="currentStep > 3"
                    step="3"
                    :color="currentStep >= 3 ? 'primary' : 'grey'"
                  >
                    Preview & Submit
                  </v-stepper-step>
                </v-stepper-header>

                <v-stepper-items>
                  <!-- Step 1: Pickup Information -->
                  <v-stepper-content step="1">
                    <v-container>
                      <v-row>
                        <!-- Pickup Location Selection -->
                        <v-col cols="12">
                          <h4 class="mb-3">Select Pickup Location</h4>
                          <p class="text-body-2 grey--text mb-4">Choose either a vendor or store to automatically fill pickup address information</p>
                        </v-col>

                        <!-- Option 1: Vendor Selection -->
                        <v-col cols="12" md="6">
                          <v-card outlined class="pa-3">
                            <v-row>
                              <v-col cols="12">
                                <h5 class="mb-2">
                                  <v-icon color="primary" left>mdi-account-tie</v-icon>
                                  Option 1: Select Vendor
                                </h5>
                                <p class="text-caption grey--text mb-3">Use vendor's registered address as pickup location</p>
                                
                                <v-autocomplete
                                  v-model="shipmentForm.selected_vendor_id"
                                  :items="vendors"
                                  label="Select Vendor"
                                  outlined
                                  dense
                                  prepend-inner-icon="mdi-store-search"
                                  clearable
                                  item-text="text"
                                  item-value="value"
                                  @change="onVendorSelected"
                                  hint="Choose vendor to auto-fill address"
                                  persistent-hint
                                ></v-autocomplete>
                              </v-col>
                            </v-row>
                          </v-card>
                        </v-col>

                        <!-- Option 2: Store Selection -->
                        <v-col cols="12" md="6">
                          <v-card outlined class="pa-3">
                            <v-row>
                              <v-col cols="12">
                                <h5 class="mb-2">
                                  <v-icon color="primary" left>mdi-store</v-icon>
                                  Option 2: Select Store
                                </h5>
                                <p class="text-caption grey--text mb-3">Use store's registered address as pickup location</p>
                                
                                <v-autocomplete
                                  v-model="shipmentForm.selected_store_id"
                                  :items="stores"
                                  label="Select Store"
                                  outlined
                                  dense
                                  prepend-inner-icon="mdi-store-outline"
                                  clearable
                                  item-text="text"
                                  item-value="value"
                                  @change="onStoreSelected"
                                  hint="Choose store to auto-fill address"
                                  persistent-hint
                                ></v-autocomplete>
                              </v-col>
                            </v-row>
                          </v-card>
                        </v-col>

                        <!-- Vendor Contact Information (shown when vendor or store is selected) -->
                        <v-col cols="12" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-divider class="my-3"></v-divider>
                          <h5 class="mb-3">
                            <v-icon color="primary" left>mdi-account-details</v-icon>
                            Vendor Contact Details
                          </h5>
                        </v-col>
                        <v-col cols="12" md="6" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-text-field
                            v-model="shipmentForm.vendor_name"
                            label="Vendor Name"
                            outlined
                            prepend-inner-icon="mdi-account"
                            :readonly="!!shipmentForm.selected_vendor_id"
                            :hint="shipmentForm.selected_vendor_id ? 'Auto-filled from vendor' : 'Enter vendor name manually'"
                            persistent-hint
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-text-field
                            v-model="shipmentForm.vendor_phone"
                            label="Vendor Phone"
                            outlined
                            type="number"
                            prepend-inner-icon="mdi-phone"
                            :readonly="!!shipmentForm.selected_vendor_id"
                            :hint="shipmentForm.selected_vendor_id ? 'Auto-filled from vendor' : 'Enter vendor phone manually'"
                            persistent-hint
                          ></v-text-field>
                        </v-col>

                        <!-- Pickup Address Information -->
                        <v-col cols="12" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-divider class="my-3"></v-divider>
                          <h5 class="mb-3">
                            <v-icon color="primary" left>mdi-map-marker</v-icon>
                            Pickup Address Details
                          </h5>
                        </v-col>
                        <v-col cols="12" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-textarea
                            v-model="shipmentForm.pickup_address"
                            label="Pickup Address"
                            outlined
                            rows="2"
                            auto-grow
                            prepend-inner-icon="mdi-map-marker"
                            :hint="shipmentForm.selected_vendor_id ? 'Auto-filled from vendor (editable)' : 'Auto-filled from store (editable)'"
                            persistent-hint
                          ></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-autocomplete
                            v-model="shipmentForm.pickup_state"
                            :items="indianStates"
                            label="Pickup State"
                            outlined
                            prepend-inner-icon="mdi-map"
                            :loading="isLoadingStates"
                            clearable
                            @change="onPickupStateChange"
                            hint="Select state to load cities"
                            persistent-hint
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-autocomplete
                            v-model="shipmentForm.pickup_city"
                            :items="pickupCities"
                            label="Pickup City"
                            outlined
                            prepend-inner-icon="mdi-city"
                            :loading="isLoadingPickupCities"
                            :disabled="!shipmentForm.pickup_state"
                            clearable
                            hint="Select state first to load cities"
                            persistent-hint
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-text-field
                            v-model="shipmentForm.pickup_pincode"
                            label="Pickup Pincode"
                            outlined
                            type="text"
                            prepend-inner-icon="mdi-mailbox"
                            maxlength="6"
                            pattern="[0-9]{6}"
                            @input="onPickupPincodeInput"
                            :loading="isCheckingPickupPincode"
                            :success="pickupPincodeServiceable === true"
                            :error="pickupPincodeServiceable === false"
                            :hint="getPickupPincodeHint()"
                            persistent-hint
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" v-if="shipmentForm.selected_vendor_id || shipmentForm.selected_store_id">
                          <v-text-field
                            v-model="shipmentForm.pickup_country"
                            label="Pickup Country"
                            outlined
                            prepend-inner-icon="mdi-earth"
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <!-- Step Actions -->
                      <v-row class="mt-4">
                        <v-col cols="12" class="text-right">
                          <v-btn
                            color="primary"
                            large
                            @click="nextStep"
                            :disabled="!validateStep1"
                          >
                            Next: Customer Information
                            <v-icon right>mdi-arrow-right</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-stepper-content>

                  <!-- Step 2: Customer Information -->
                  <v-stepper-content step="2">
                    <v-container>
                      <v-row>
                        <!-- Customer Information -->
                        <v-col cols="12">
                          <h4 class="mb-3">Customer Information</h4>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="shipmentForm.customer_name"
                            label="Customer Name"
                            outlined
                            prepend-inner-icon="mdi-account"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="shipmentForm.customer_phone"
                            label="Customer Phone"
                            outlined
                            type="number"
                            prepend-inner-icon="mdi-phone"
                          ></v-text-field>
                        </v-col>
                        
                        <!-- Customer Address Selection Options -->
                        <v-col cols="12">
                          <v-divider class="my-3"></v-divider>
                          <h5 class="mb-3">
                            <v-icon color="primary" left>mdi-map-marker</v-icon>
                            Customer Delivery Address
                          </h5>
                          <p class="text-body-2 grey--text mb-4">Choose address source or enter custom address</p>
                        </v-col>

                        <!-- Address Type Selection -->
                        <v-col cols="12">
                          <v-radio-group
                            v-model="customerAddressType"
                            row
                            @change="onCustomerAddressTypeChange"
                            class="mt-0"
                          >
                            <v-radio
                              label="Customer Address"
                              value="customer"
                              color="primary"
                            ></v-radio>
                            <v-radio
                              label="Select Store Address"
                              value="store"
                              color="primary"
                            ></v-radio>
                            <v-radio
                              label="Enter Custom Address"
                              value="custom"
                              color="primary"
                            ></v-radio>
                          </v-radio-group>
                        </v-col>

                        <!-- Store Selection for Customer Address -->
                        <v-col cols="12" v-if="customerAddressType === 'store'">
                          <v-autocomplete
                            v-model="selectedCustomerStoreId"
                            :items="stores"
                            label="Select Store for Delivery Address"
                            outlined
                            dense
                            prepend-inner-icon="mdi-store-outline"
                            clearable
                            item-text="text"
                            item-value="value"
                            @change="onCustomerStoreSelected"
                            hint="Choose store to auto-fill delivery address"
                            persistent-hint
                          ></v-autocomplete>
                        </v-col>

                        <!-- Customer Address (from existing data) -->
                        <v-col cols="12" v-if="customerAddressType === 'customer'">
                          <v-textarea
                            v-model="shipmentForm.customer_address"
                            label="Customer Address"
                            outlined
                            rows="2"
                            auto-grow
                            prepend-inner-icon="mdi-home"
                            hint="Auto-filled from customer information"
                            persistent-hint
                          ></v-textarea>
                        </v-col>

                        <!-- Store Address (from store selection) -->
                        <v-col cols="12" v-if="customerAddressType === 'store' && selectedCustomerStoreId">
                          <v-textarea
                            v-model="shipmentForm.customer_address"
                            label="Delivery Address"
                            outlined
                            rows="2"
                            auto-grow
                            prepend-inner-icon="mdi-home"
                            hint="Auto-filled from selected store"
                            persistent-hint
                          ></v-textarea>
                        </v-col>

                        <!-- Custom Address Fields -->
                        <v-col cols="12" v-if="customerAddressType === 'custom'">
                          <v-textarea
                            v-model="shipmentForm.customer_address"
                            label="Custom Address"
                            outlined
                            rows="1"
                            auto-grow
                            prepend-inner-icon="mdi-home"
                            placeholder="Enter custom delivery address"
                          ></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="shipmentForm.customer_state"
                            :items="indianStates"
                            label="State"
                            outlined
                            prepend-inner-icon="mdi-map"
                            :loading="isLoadingStates"
                            clearable
                            @change="onCustomerStateChange"
                            :hint="isFetchingPincodeLocation ? 'Auto-fetching from pincode...' : ''"
                            persistent-hint
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="shipmentForm.customer_city"
                            :items="customerCities"
                            label="City"
                            outlined
                            prepend-inner-icon="mdi-city"
                            :loading="isLoadingCustomerCities"
                            :disabled="!shipmentForm.customer_state"
                            clearable
                            hint="Select state first to load cities"
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="shipmentForm.customer_pincode"
                            label="Pincode"
                            outlined
                            type="text"
                            prepend-inner-icon="mdi-mailbox"
                            maxlength="6"
                            pattern="[0-9]{6}"
                            @input="onCustomerPincodeInput"
                            :loading="isCheckingCustomerPincode || isFetchingPincodeLocation"
                            :success="customerPincodeServiceable === true"
                            :error="customerPincodeServiceable === false"
                            :hint="getCustomerPincodeHint()"
                            persistent-hint
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="shipmentForm.customer_country"
                            label="Country"
                            outlined
                            prepend-inner-icon="mdi-earth"
                            :hint="''"
                          ></v-text-field>
                        </v-col>

                      </v-row>

                      <!-- Step Actions -->
                      <v-row class="mt-4">
                        <v-col cols="12">
                          <v-btn color="grey" text large @click="previousStep">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Back: Pickup Information
                          </v-btn>
                          <v-btn
                            color="primary"
                            large
                            @click="nextStep"
                            :disabled="!validateStep2"
                            class="float-right"
                          >
                            Next: Preview & Submit
                            <v-icon right>mdi-arrow-right</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-stepper-content>

                  <!-- Step 3: Preview & Submit -->
                  <v-stepper-content step="3">
                    <v-container>
                      <v-row>
                        <!-- Validation Warning Alert -->
                        <v-col cols="12" v-if="hasGiftCatalogValidationIssues">
                          <v-alert
                            type="warning"
                            prominent
                            border="left"
                            colored-border
                            elevation="2"
                          >
                            <v-row align="center">
                              <v-col class="grow">
                                <div class="font-weight-bold mb-2">Missing Gift Catalog Information</div>
                                <div>The following required information is missing from the gift catalog:</div>
                                <ul class="mt-2">
                                  <li v-if="!shipmentForm.total_amount">Gift catalog amount</li>
                                  <li v-if="!giftCatalogDimensions.width">Width dimension</li>
                                  <li v-if="!giftCatalogDimensions.height">Height dimension</li>
                                  <li v-if="!giftCatalogDimensions.length">Length dimension</li>
                                  <li v-if="!giftCatalogDimensions.weight">Weight</li>
                                </ul>
                                <div class="mt-2">Please update the gift catalog record before creating this shipment.</div>
                              </v-col>
                            </v-row>
                          </v-alert>
                        </v-col>

                        <!-- Gift Catalog Preview -->
                        <v-col cols="12">
                          <v-card outlined>
                            <v-card-title class="py-2">
                              <v-icon left color="primary">mdi-gift</v-icon>
                              Gift Catalog Information
                            </v-card-title>
                            <v-card-text class="py-2">
                              <v-row>
                                <v-col cols="12" md="6">
                                  <strong>Gift Catalog:</strong>
                                  {{ giftCatalogName }}
                                </v-col>
                                <v-col cols="12" md="3">
                                  <strong>Quantity:</strong>
                                  {{ shipmentForm.quantity }}
                                </v-col>
                                <v-col cols="12" md="3">
                                  <strong>Total Amount:</strong>
                                  <span :class="!shipmentForm.total_amount ? 'error--text' : ''">
                                    {{ shipmentForm.total_amount ? `₹${shipmentForm.total_amount}` : 'Not provided' }}
                                  </span>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <strong>Dimensions:</strong>
                                  <span :class="!giftCatalogDimensions.width ? 'error--text' : ''">
                                    {{ giftCatalogDimensions.width ? `${giftCatalogDimensions.width}cm (W)` : 'Width not provided' }}
                                  </span>
                                  <span :class="!giftCatalogDimensions.height ? 'error--text' : ''">
                                    {{ giftCatalogDimensions.height ? ` × ${giftCatalogDimensions.height}cm (H)` : ' × Height not provided' }}
                                  </span>
                                  <span :class="!giftCatalogDimensions.length ? 'error--text' : ''">
                                    {{ giftCatalogDimensions.length ? ` × ${giftCatalogDimensions.length}cm (L)` : ' × Length not provided' }}
                                  </span>
                                  <span :class="!giftCatalogDimensions.weight ? 'error--text' : ''">
                                    {{ giftCatalogDimensions.weight ? `, ${giftCatalogDimensions.weight}g` : ', Weight not provided' }}
                                  </span>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <strong>HSN Code:</strong>
                                  {{ giftCatalogHsnCode || 'Not provided' }}
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <!-- Vendor & Pickup Information Preview -->
                        <v-col cols="12" md="6">
                          <v-card outlined>
                            <v-card-title class="py-2">
                              <v-icon left color="primary">mdi-truck</v-icon>
                              Pickup Information
                            </v-card-title>
                            <v-card-text class="py-2">
                              <div>
                                <strong>Vendor:</strong>
                                {{
                                  shipmentForm.vendor_name || "Not specified"
                                }}
                              </div>
                              <div>
                                <strong>Phone:</strong>
                                {{
                                  shipmentForm.vendor_phone || "Not specified"
                                }}
                              </div>
                              <div class="mt-2">
                                <strong>Pickup Address:</strong>
                              </div>
                              <div>
                                {{
                                  shipmentForm.pickup_address || "Not specified"
                                }}
                              </div>
                              <div>
                                {{ shipmentForm.pickup_city }},
                                {{ shipmentForm.pickup_state }}
                                {{ shipmentForm.pickup_pincode }}
                              </div>
                              <div>{{ shipmentForm.pickup_country }}</div>
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <!-- Customer Information Preview -->
                        <v-col cols="12" md="6">
                          <v-card outlined>
                            <v-card-title class="py-2">
                              <v-icon left color="primary">mdi-account</v-icon>
                              Customer Information
                            </v-card-title>
                            <v-card-text class="py-2">
                              <div>
                                <strong>Name:</strong>
                                {{
                                  shipmentForm.customer_name || "Not specified"
                                }}
                              </div>
                              <div>
                                <strong>Phone:</strong>
                                {{
                                  shipmentForm.customer_phone || "Not specified"
                                }}
                              </div>
                              <div class="mt-2">
                                <strong>Delivery Address:</strong>
                              </div>
                              <div>
                                {{
                                  shipmentForm.customer_address ||
                                  "Not specified"
                                }}
                              </div>
                              <div>
                                {{ shipmentForm.customer_city }},
                                {{ shipmentForm.customer_state }}
                                {{ shipmentForm.customer_pincode }}
                              </div>
                              <div>{{ shipmentForm.customer_country }}</div>
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <!-- Return Information -->
                        <v-col cols="12">
                          <v-card outlined>
                            <v-card-title class="py-2">
                              <v-icon left color="primary"
                                >mdi-keyboard-return</v-icon
                              >
                              Return Information
                            </v-card-title>
                            <v-card-text class="py-2">
                              <v-checkbox
                                v-model="sameAsPickupInfo"
                                label="Same as Pickup Information"
                                color="primary"
                                @change="onSameAsPickupChange"
                                hint="Check this to use pickup information for return details"
                                persistent-hint
                                class="mt-0"
                              ></v-checkbox>

                              <v-row class="mt-2">
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    v-model="shipmentForm.return_phone"
                                    label="Return Phone"
                                    outlined
                                    prepend-inner-icon="mdi-phone"
                                    :disabled="sameAsPickupInfo"
                                    :rules="returnFieldRules"
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6"></v-col>
                                <v-col cols="12">
                                  <v-textarea
                                    v-model="shipmentForm.return_address"
                                    label="Return Address"
                                    outlined
                                    rows="1"
                                    auto-grow
                                    prepend-inner-icon="mdi-home"
                                    :disabled="sameAsPickupInfo"
                                    :rules="returnFieldRules"
                                  ></v-textarea>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-autocomplete
                                    v-model="shipmentForm.return_state"
                                    :items="indianStates"
                                    label="Return State"
                                    outlined
                                    prepend-inner-icon="mdi-map"
                                    :loading="isLoadingStates"
                                    :disabled="sameAsPickupInfo"
                                    clearable
                                    @change="onReturnStateChange"
                                    :rules="returnFieldRules"
                                    hint="Select state to load cities"
                                    persistent-hint
                                  ></v-autocomplete>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-autocomplete
                                    v-model="shipmentForm.return_city"
                                    :items="returnCities"
                                    label="Return City"
                                    outlined
                                    prepend-inner-icon="mdi-city"
                                    :loading="isLoadingReturnCities"
                                    :disabled="sameAsPickupInfo || !shipmentForm.return_state"
                                    clearable
                                    :rules="returnFieldRules"
                                    hint="Select state first to load cities"
                                    persistent-hint
                                  ></v-autocomplete>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    v-model="shipmentForm.return_pincode"
                                    label="Return Pincode"
                                    outlined
                                    type="text"
                                    prepend-inner-icon="mdi-mailbox"
                                    :disabled="sameAsPickupInfo"
                                    :rules="returnFieldRules"
                                    maxlength="6"
                                    pattern="[0-9]{6}"
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    v-model="shipmentForm.return_country"
                                    label="Return Country"
                                    outlined
                                    prepend-inner-icon="mdi-earth"
                                    :disabled="sameAsPickupInfo"
                                    :rules="returnFieldRules"
                                  ></v-text-field>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <!-- Remarks -->
                        <v-col cols="12">
                          <v-card outlined>
                            <v-card-title class="py-2">
                              <v-icon left color="primary"
                                >mdi-note-text</v-icon
                              >
                              Additional Information
                            </v-card-title>
                            <v-card-text class="py-2">
                              <v-textarea
                                v-model="shipmentForm.remarks"
                                label="Remarks (Optional)"
                                outlined
                                rows="3"
                                prepend-inner-icon="mdi-note-text"
                                hint="Add any special instructions or notes"
                                persistent-hint
                              ></v-textarea>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>

                      <!-- Step Actions -->
                      <v-row class="mt-4">
                        <v-col cols="12">
                          <v-btn color="grey" text large @click="previousStep">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Back: Customer Information
                          </v-btn>
                          <v-btn
                            color="primary"
                            large
                            :loading="isCreatingShipment"
                            @click="createShipment"
                            class="float-right"
                          >
                            <v-icon left>mdi-check</v-icon>
                            Create Shipment
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="shipmentCreatedDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Shipment created successfully.</h3>
                      <br />
                      <v-btn color="success" @click="goBackToGiftOrders">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "CreateShipmentPage",
  data() {
    return {
      currentStep: 1,
      vendors: [],
      stores: [],
      isCreatingShipment: false,
      shipmentCreatedDialog: false,
      isCheckingServiceability: false,
      isCheckingPickupPincode: false,
      isCheckingCustomerPincode: false,
      pickupPincodeServiceable: null,
      customerPincodeServiceable: null,
      isFetchingPincodeLocation: false,
      giftOrderData: null,
      giftCatalogName: "",
      giftCatalogDimensions: {
        width: "",
        height: "",
        length: "",
        weight: "",
      },
      giftCatalogHsnCode: "",
      sameAsPickupInfo: false,
      customerAddressType: 'customer', // 'customer', 'store', 'custom'
      selectedCustomerStoreId: null,
      // Indian States and Cities
      indianStates: [],
      pickupCities: [],
      customerCities: [],
      returnCities: [],
      isLoadingStates: false,
      isLoadingPickupCities: false,
      isLoadingCustomerCities: false,
      isLoadingReturnCities: false,
      // Store original state/city values for matching
      originalPickupState: '',
      originalPickupCity: '',
      originalCustomerState: '',
      originalCustomerCity: '',
      originalCustomerData: {
        address: '',
        city: '',
        state: '',
        pincode: '',
        country: ''
      },
      shipmentForm: {
        gift_order_id: "",
        gift_catalog_id: "",
        customer_id: "",
        vendor_id: "",
        selected_vendor_id: "",
        store_id: "",
        selected_store_id: "",
        customer_name: "",
        customer_phone: "",
        customer_address: "",
        customer_city: "",
        customer_state: "",
        customer_pincode: "",
        customer_country: "",
        vendor_name: "",
        vendor_phone: "",
        pickup_address: "",
        pickup_city: "",
        pickup_state: "",
        pickup_pincode: "",
        pickup_country: "",
        return_phone: "",
        return_address: "",
        return_city: "",
        return_state: "",
        return_pincode: "",
        return_country: "",
        total_amount: "",
        quantity: "",
        remarks: "",
        is_active: true,
        same_as_pickup_info: false,
      },
    };
  },
  computed: {
    hasGiftCatalogValidationIssues() {
      // Check if any required gift catalog information is missing
      return (
        !this.shipmentForm.total_amount ||
        !this.giftCatalogDimensions.width ||
        !this.giftCatalogDimensions.height ||
        !this.giftCatalogDimensions.length ||
        !this.giftCatalogDimensions.weight
      );
    },
    returnFieldRules() {
      if (this.sameAsPickupInfo) {
        return []; // No validation rules when using pickup info
      }
      return [
        (v) =>
          !!v || "This field is required when not using pickup information",
      ];
    },
    validateStep1() {
      // Must have either vendor or store selected
      const hasVendorOrStore = this.shipmentForm.selected_vendor_id || this.shipmentForm.selected_store_id;
      
      // Must have vendor contact details filled
      const hasVendorDetails = !!(
        this.shipmentForm.vendor_name &&
        this.shipmentForm.vendor_phone
      );
      
      // Must have pickup address details filled
      const hasPickupDetails = !!(
        this.shipmentForm.pickup_address &&
        this.shipmentForm.pickup_city &&
        this.shipmentForm.pickup_state &&
        this.shipmentForm.pickup_pincode &&
        this.shipmentForm.pickup_country
      );
      
      return hasVendorOrStore && hasVendorDetails && hasPickupDetails;
    },
    validateStep2() {
      return !!(
        this.shipmentForm.customer_name &&
        this.shipmentForm.customer_phone &&
        this.shipmentForm.customer_address &&
        this.shipmentForm.customer_city &&
        this.shipmentForm.customer_state &&
        this.shipmentForm.customer_pincode &&
        this.shipmentForm.customer_country
      );
    },
  },
  mounted() {
    this.getVendors();
    this.getStores();
    this.loadGiftOrderData();
    this.fetchIndianStates();
  },
  methods: {
    async getVendors() {
      try {
        const response = await axios.get("/vendors/dropdown");
        this.vendors = response.data.data.map((vendor) => ({
          id: vendor.id,
          text: vendor.name + (vendor.location ? ` (${vendor.location})` : ""),
          value: vendor.id,
          vendor: vendor,
        }));
      } catch (error) {
        console.error("Error fetching vendors:", error);
        this.$toast.error("Failed to load vendors");
      }
    },

    async getStores() {
      try {
        const response = await axios.get("/stores/dropdown");
        this.stores = response.data.data.map((store) => ({
          id: store.id,
          text:
            store.business_name +
            (store.store_location ? ` (${store.store_location})` : ""),
          value: store.id,
          store: store,
        }));
      } catch (error) {
        console.error("Error fetching stores:", error);
        this.$toast.error("Failed to load stores");
      }
    },

    async fetchIndianStates() {
      try {
        this.isLoadingStates = true;
        const response = await axios.post(
          "https://countriesnow.space/api/v0.1/countries/states",
          { country: "India" }
        );

        if (response.data && response.data.data && response.data.data.states) {
          this.indianStates = response.data.data.states.map((state) => state.name);

          // After loading states, match existing pickup state if any
          if (this.originalPickupState) {
            this.matchAndSetState(this.originalPickupState, 'pickup');
          }
          // Also match customer state if any
          if (this.originalCustomerState) {
            this.matchAndSetState(this.originalCustomerState, 'customer');
          }
        }
      } catch (error) {
        console.error("Error fetching Indian states:", error);
        // Fallback to common Indian states
        this.indianStates = [
          "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
          "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
          "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
          "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
          "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
          "Uttar Pradesh", "Uttarakhand", "West Bengal",
          "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
          "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
        ];

        // After loading states, match existing pickup state if any
        if (this.originalPickupState) {
          this.matchAndSetState(this.originalPickupState, 'pickup');
        }
        // Also match customer state if any
        if (this.originalCustomerState) {
          this.matchAndSetState(this.originalCustomerState, 'customer');
        }
      } finally {
        this.isLoadingStates = false;
      }
    },

    async fetchCitiesForState(stateName, type = 'pickup') {
      if (!stateName) {
        if (type === 'pickup') {
          this.pickupCities = [];
        } else if (type === 'customer') {
          this.customerCities = [];
        } else if (type === 'return') {
          this.returnCities = [];
        }
        return;
      }

      try {
        if (type === 'pickup') {
          this.isLoadingPickupCities = true;
        } else if (type === 'customer') {
          this.isLoadingCustomerCities = true;
        } else if (type === 'return') {
          this.isLoadingReturnCities = true;
        }

        const response = await axios.post(
          "https://countriesnow.space/api/v0.1/countries/state/cities",
          { country: "India", state: stateName }
        );

        if (response.data && response.data.data) {
          const cities = response.data.data;
          if (type === 'pickup') {
            this.pickupCities = cities;

            // Match existing city if any
            if (this.originalPickupCity) {
              this.matchAndSetCity(this.originalPickupCity, 'pickup');
            }
          } else if (type === 'customer') {
            this.customerCities = cities;

            // Match existing city if any
            if (this.originalCustomerCity) {
              this.matchAndSetCity(this.originalCustomerCity, 'customer');
              // Fallback: if city still not matched, add it to list and select it
              if (!this.shipmentForm.customer_city) {
                if (!this.customerCities.includes(this.originalCustomerCity)) {
                  this.customerCities.push(this.originalCustomerCity);
                }
                this.shipmentForm.customer_city = this.originalCustomerCity;
              }
            }
          } else if (type === 'return') {
            this.returnCities = cities;
          }
        }
      } catch (error) {
        console.error(`Error fetching cities for ${stateName}:`, error);
        // Clear cities on error
        if (type === 'pickup') {
          this.pickupCities = [];
        } else if (type === 'customer') {
          this.customerCities = [];
        } else if (type === 'return') {
          this.returnCities = [];
        }
      } finally {
        if (type === 'pickup') {
          this.isLoadingPickupCities = false;
        } else if (type === 'customer') {
          this.isLoadingCustomerCities = false;
        } else if (type === 'return') {
          this.isLoadingReturnCities = false;
        }
      }
    },

    matchAndSetState(originalState, type = 'pickup') {
      if (!originalState || this.indianStates.length === 0) return;

      const normalizedOriginal = originalState.toLowerCase().trim();

      // Try exact match first
      let matchedState = this.indianStates.find(
        (state) => state.toLowerCase() === normalizedOriginal
      );

      // Try partial match if exact match not found
      if (!matchedState) {
        matchedState = this.indianStates.find(
          (state) =>
            state.toLowerCase().includes(normalizedOriginal) ||
            normalizedOriginal.includes(state.toLowerCase())
        );
      }

      if (matchedState) {
        if (type === 'pickup') {
          this.shipmentForm.pickup_state = matchedState;
          // Fetch cities for the matched state
          this.fetchCitiesForState(matchedState, 'pickup');
        } else if (type === 'customer') {
          this.shipmentForm.customer_state = matchedState;
          // Fetch cities for the matched state
          this.fetchCitiesForState(matchedState, 'customer');
        } else if (type === 'return') {
          this.shipmentForm.return_state = matchedState;
          // Fetch cities for the matched state
          this.fetchCitiesForState(matchedState, 'return');
        }
      }
    },

    matchAndSetCity(originalCity, type = 'pickup') {
      if (!originalCity) return;

      let cities = [];
      if (type === 'pickup') {
        cities = this.pickupCities;
      } else if (type === 'customer') {
        cities = this.customerCities;
      } else if (type === 'return') {
        cities = this.returnCities;
      }
      if (cities.length === 0) return;

      const normalizedOriginal = originalCity.toLowerCase().trim();

      // Try exact match first
      let matchedCity = cities.find(
        (city) => city.toLowerCase() === normalizedOriginal
      );

      // Try partial match if exact match not found
      if (!matchedCity) {
        matchedCity = cities.find(
          (city) =>
            city.toLowerCase().includes(normalizedOriginal) ||
            normalizedOriginal.includes(city.toLowerCase())
        );
      }

      if (matchedCity) {
        if (type === 'pickup') {
          this.shipmentForm.pickup_city = matchedCity;
        } else if (type === 'customer') {
          this.shipmentForm.customer_city = matchedCity;
        } else if (type === 'return') {
          this.shipmentForm.return_city = matchedCity;
        }
      }
    },

    onPickupStateChange(stateName) {
      // Clear the city when state changes
      this.shipmentForm.pickup_city = "";
      this.pickupCities = [];

      if (stateName) {
        this.fetchCitiesForState(stateName, 'pickup');
      }

      // Update return information if "Same as Pickup" is checked
      if (this.sameAsPickupInfo) {
        this.copyPickupToReturn();
      }
    },

    onCustomerStateChange(stateName) {
      // Clear the city when state changes
      this.shipmentForm.customer_city = "";
      this.customerCities = [];

      if (stateName) {
        this.fetchCitiesForState(stateName, 'customer');
      }
    },

    onReturnStateChange(stateName) {
      // Clear the city when state changes
      this.shipmentForm.return_city = "";
      this.returnCities = [];

      if (stateName) {
        this.fetchCitiesForState(stateName, 'return');
      }
    },

    async loadGiftOrderData() {
      // Get gift order ID from route query parameters
      const { giftOrderId } = this.$route.query;

      if (giftOrderId) {
        try {
          // Encrypt the gift order ID before making API call
          const encryptedGiftOrderId = this.encryptIt(giftOrderId);

          // Fetch gift order data with relationships from API
          const response = await axios.get(
            `/place_orders/${encryptedGiftOrderId}`
          );

          if (response.data && response.data.data) {
            const giftOrder = response.data.data;

            // Populate form with API data
            this.shipmentForm.gift_order_id = giftOrder.id;
            this.shipmentForm.gift_catalog_id = giftOrder.gift_catalog_id || "";
            this.shipmentForm.customer_id = giftOrder.user
              ? giftOrder.user.id
              : "";
            this.shipmentForm.customer_name = giftOrder.user
              ? giftOrder.user.user_name
              : "";
            this.shipmentForm.customer_phone = giftOrder.user
              ? String(giftOrder.user.phone || "")
              : "";
            this.shipmentForm.customer_address = this.buildAddress(giftOrder);
            // Store original customer state/city for matching with API data
            this.originalCustomerState = giftOrder.state || null;
            this.originalCustomerCity = giftOrder.city || null;

            // Set customer pincode and country
            this.shipmentForm.customer_pincode = String(
              giftOrder.pin_code || ""
            );
            this.shipmentForm.customer_country = giftOrder.country || "India";

            // Set initial state/city from stored data (will be corrected by pincode fetch on step 2)
            if (this.indianStates.length > 0) {
              this.matchAndSetState(this.originalCustomerState, 'customer');
            } else {
              this.shipmentForm.customer_state = this.originalCustomerState;
              this.shipmentForm.customer_city = this.originalCustomerCity;
            }

            // Store original customer data for restoration when switching back to 'customer' address type
            this.originalCustomerData = {
              address: this.shipmentForm.customer_address,
              city: this.originalCustomerCity,
              state: this.originalCustomerState,
              pincode: this.shipmentForm.customer_pincode,
              country: this.shipmentForm.customer_country
            };

            // Log customer data for debugging
            console.log("Gift order customer data loaded:", {
              customer_city: this.shipmentForm.customer_city,
              customer_state: this.shipmentForm.customer_state,
              customer_pincode: this.shipmentForm.customer_pincode,
              original_city: giftOrder.city,
              original_state: giftOrder.state,
            });
            this.shipmentForm.quantity = giftOrder.quantity || "1";
            this.shipmentForm.remarks = giftOrder.remarks || "";
            this.shipmentForm.pickup_country = "India";

            // Set gift catalog data from API response
            if (giftOrder.gift_catalog) {
              this.giftCatalogName =
                giftOrder.gift_catalog.name || "Unknown Gift Catalog";

              // Set total amount from gift catalog (just the number, field has rupee icon)
              if (giftOrder.gift_catalog.amount) {
                this.shipmentForm.total_amount = parseFloat(
                  giftOrder.gift_catalog.amount
                ).toFixed(2);
              }

              // Set dimensions from gift catalog
              this.giftCatalogDimensions = {
                width: giftOrder.gift_catalog.width || "",
                height: giftOrder.gift_catalog.height || "",
                length: giftOrder.gift_catalog.length || "",
                weight: giftOrder.gift_catalog.weight || "",
              };

              // Set HSN code from gift catalog
              this.giftCatalogHsnCode = giftOrder.gift_catalog.hsn_code || "";
            } else {
              this.giftCatalogName = "Unknown Gift Catalog";
            }
          }
        } catch (error) {
          console.error("Error fetching gift order:", error);
          this.$toast.error("Failed to load gift order data");
        }
      }
    },

    buildAddress(giftOrder) {
      const address1 = giftOrder.address_line1 || "";
      const address2 = giftOrder.address_line2 || "";
      return address1 + (address2 ? ", " + address2 : "");
    },

    onVendorSelected(vendorId) {
      if (vendorId) {
        // Clear store selection when vendor is selected
        this.shipmentForm.selected_store_id = "";
        this.shipmentForm.store_id = "";

        // Find the selected vendor
        const selectedVendorItem = this.vendors.find(
          (v) => v.value === vendorId
        );
        if (selectedVendorItem) {
          const vendor = selectedVendorItem.vendor;

          // Auto-fill vendor information
          this.shipmentForm.vendor_id = vendor.id;
          this.shipmentForm.vendor_name = vendor.name || "";
          this.shipmentForm.vendor_phone = String(vendor.phone || "");

          // Store original state/city for matching
          this.originalPickupState = vendor.state || "";
          this.originalPickupCity = vendor.city || "";

          // Auto-fill pickup information from vendor location
          this.shipmentForm.pickup_address = vendor.address || "";
          this.shipmentForm.pickup_pincode = String(vendor.pincode || "");
          this.shipmentForm.pickup_country = vendor.country || "India";

          // Match state and city with API data
          if (this.indianStates.length > 0) {
            this.matchAndSetState(this.originalPickupState);
          } else {
            // States not loaded yet, will match when loaded
            this.shipmentForm.pickup_state = vendor.state || "";
            this.shipmentForm.pickup_city = vendor.city || "";
          }

          // Automatically check pickup pincode serviceability if pincode is available
          const pincodeStr = String(vendor.pincode || "");
          if (pincodeStr && pincodeStr.length === 6 && /^\d{6}$/.test(pincodeStr)) {
            this.checkIndividualPincodeServiceability(pincodeStr, 'pickup');
          }

          // Update return information if "Same as Pickup" is checked
          if (this.sameAsPickupInfo) {
            this.copyPickupToReturn();
          }
        }
      } else {
        // Clear vendor and pickup information when no vendor is selected
        this.clearVendorAndPickupInfo();
      }
    },

    onStoreSelected(storeId) {
      if (storeId) {
        // Clear vendor selection dropdown only (keep vendor contact fields for manual entry)
        this.shipmentForm.selected_vendor_id = "";
        this.shipmentForm.vendor_id = "";

        // Find the selected store
        const selectedStoreItem = this.stores.find((s) => s.value === storeId);
        if (selectedStoreItem) {
          const store = selectedStoreItem.store;

          // Auto-fill store information
          this.shipmentForm.store_id = store.id;

          // Store original state/city for matching
          this.originalPickupState = store.state_jurisdiction || "";
          this.originalPickupCity = store.city || "";

          // Auto-fill pickup information from store location
          this.shipmentForm.pickup_address =
            store.new_address || store.address || "";
          this.shipmentForm.pickup_pincode = String(store.pincode || "");
          this.shipmentForm.pickup_country = "India";

          // Match state and city with API data
          if (this.indianStates.length > 0) {
            this.matchAndSetState(this.originalPickupState);
          } else {
            // States not loaded yet, will match when loaded
            this.shipmentForm.pickup_state = store.state_jurisdiction || "";
            this.shipmentForm.pickup_city = store.city || "";
          }

          // Automatically check pickup pincode serviceability if pincode is available
          const pincodeStr = String(store.pincode || "");
          if (pincodeStr && pincodeStr.length === 6 && /^\d{6}$/.test(pincodeStr)) {
            this.checkIndividualPincodeServiceability(pincodeStr, 'pickup');
          }

          // Update return information if "Same as Pickup" is checked
          if (this.sameAsPickupInfo) {
            this.copyPickupToReturn();
          }
        }
      } else {
        // Clear store and pickup information when no store is selected
        this.clearStoreAndPickupInfo();
      }
    },

    async checkPincodeServiceability() {
      if (
        !this.shipmentForm.pickup_pincode ||
        !this.shipmentForm.customer_pincode
      ) {
        this.$toast.error("Please fill in both pickup and delivery pincodes");
        return false;
      }

      try {
        this.isCheckingServiceability = true;

        const response = await axios.post("/delhivery/check-serviceability", {
          pickup_pincode: String(this.shipmentForm.pickup_pincode),
          delivery_pincode: String(this.shipmentForm.customer_pincode),
        });

        this.isCheckingServiceability = false;

        if (response.data.success) {
          this.$toast.success("Pincode serviceability checked successfully");
          return true;
        } else {
          this.$toast.error(response.data.message || "Pincode not serviceable");
          return false;
        }
      } catch (error) {
        this.isCheckingServiceability = false;
        console.error("Error checking serviceability:", error);

        let errorMessage = "Failed to check pincode serviceability";

        if (error.response && error.response.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }

          // Handle specific error cases
          if (error.response.status === 401) {
            errorMessage =
              "Delhivery API authentication failed. Please contact admin.";
          } else if (error.response.status >= 500) {
            errorMessage =
              "Delhivery service temporarily unavailable. Please try again later.";
          }
        }

        this.$toast.error(errorMessage);
        return false;
      }
    },

    async createShipment() {
      try {
        this.isCreatingShipment = true;

        // Validate return information if not using pickup info
        if (!this.sameAsPickupInfo) {
          const returnFieldsValid = this.validateReturnFields();
          if (!returnFieldsValid) {
            this.isCreatingShipment = false;
            return;
          }
        }

        // Check pincode serviceability first
        const isServiceable = await this.checkPincodeServiceability();

        if (!isServiceable) {
          this.isCreatingShipment = false;
          return;
        }

        await axios.post("/deliveries", this.shipmentForm);

        this.isCreatingShipment = false;
        this.shipmentCreatedDialog = true;
      } catch (error) {
        this.isCreatingShipment = false;
        console.error("Error creating shipment:", error);

        // Extract and display the actual error message from the backend
        let errorMessage = "Failed to create shipment. Please try again.";

        if (error.response && error.response.data) {
          // Check for error message in response
          if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.errors) {
            // Handle Laravel validation errors
            const errors = error.response.data.errors;
            const firstError = Object.values(errors)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          }
        }

        this.$toast.error(errorMessage);

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          console.log("Validation errors:", error.response.data.errors);
        }
      }
    },

    goBack() {
      this.$router.go(-1);
    },

    goBackToGiftOrders() {
      this.shipmentCreatedDialog = false;
      this.$router.push("/gift-orders");
    },

    onSameAsPickupChange() {
      if (this.sameAsPickupInfo) {
        // Copy pickup information to return fields
        this.copyPickupToReturn();
        this.shipmentForm.same_as_pickup_info = true;
        this.$toast.info("Return information set to pickup information");
      } else {
        // Clear return fields when unchecked
        this.clearReturnFields();
        this.shipmentForm.same_as_pickup_info = false;
        this.$toast.info(
          "Return information cleared - please fill in custom details"
        );
      }
    },

    copyPickupToReturn() {
      this.shipmentForm.return_phone = String(
        this.shipmentForm.vendor_phone || ""
      );
      this.shipmentForm.return_address = this.shipmentForm.pickup_address || "";
      this.shipmentForm.return_state = this.shipmentForm.pickup_state || "";
      this.shipmentForm.return_city = this.shipmentForm.pickup_city || "";
      this.shipmentForm.return_pincode = String(
        this.shipmentForm.pickup_pincode || ""
      );
      this.shipmentForm.return_country =
        this.shipmentForm.pickup_country || "India";

      // Copy pickup cities to return cities for dropdown
      this.returnCities = [...this.pickupCities];
    },

    clearReturnFields() {
      this.shipmentForm.return_phone = "";
      this.shipmentForm.return_address = "";
      this.shipmentForm.return_city = "";
      this.shipmentForm.return_state = "";
      this.shipmentForm.return_pincode = "";
      this.shipmentForm.return_country = "";
      this.returnCities = [];
    },

    validateReturnFields() {
      const requiredFields = [
        { field: "return_address", label: "Return Address" },
        { field: "return_city", label: "Return City" },
        { field: "return_state", label: "Return State" },
        { field: "return_pincode", label: "Return Pincode" },
      ];

      for (const { field, label } of requiredFields) {
        if (
          !this.shipmentForm[field] ||
          this.shipmentForm[field].toString().trim() === ""
        ) {
          this.$toast.error(
            `${label} is required when not using pickup information`
          );
          return false;
        }
      }

      return true;
    },

    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++;

        // When entering step 2, fetch state/city from pincode and check serviceability
        if (this.currentStep === 2) {
          this.$nextTick(() => {
            if (this.customerAddressType === 'customer' && this.shipmentForm.customer_pincode) {
              const pincodeStr = String(this.shipmentForm.customer_pincode);
              if (pincodeStr.length === 6 && /^\d{6}$/.test(pincodeStr)) {
                this.checkIndividualPincodeServiceability(pincodeStr, 'customer');
                this.fetchLocationFromPincode(pincodeStr);
              }
            }
          });
        }
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    clearVendorAndPickupInfo() {
      // Clear vendor information
      this.shipmentForm.vendor_id = "";
      this.shipmentForm.vendor_name = "";
      this.shipmentForm.vendor_phone = "";
      
      // Clear pickup information
      this.shipmentForm.pickup_address = "";
      this.shipmentForm.pickup_city = "";
      this.shipmentForm.pickup_state = "";
      this.shipmentForm.pickup_pincode = "";
      this.shipmentForm.pickup_country = "";
    },

    clearStoreAndPickupInfo() {
      // Clear store information
      this.shipmentForm.store_id = "";
      
      // Clear pickup information
      this.shipmentForm.pickup_address = "";
      this.shipmentForm.pickup_city = "";
      this.shipmentForm.pickup_state = "";
      this.shipmentForm.pickup_pincode = "";
      this.shipmentForm.pickup_country = "";
    },

    onPickupPincodeInput(value) {
      // Reset serviceability status when input changes
      this.pickupPincodeServiceable = null;
      
      // Only check if exactly 6 digits
      if (value && value.length === 6 && /^\d{6}$/.test(value)) {
        this.checkIndividualPincodeServiceability(value, 'pickup');
      }
    },

    onCustomerPincodeInput(value) {
      // Reset serviceability status when input changes
      this.customerPincodeServiceable = null;

      // Only check if exactly 6 digits
      if (value && value.length === 6 && /^\d{6}$/.test(value)) {
        this.checkIndividualPincodeServiceability(value, 'customer');
        this.fetchLocationFromPincode(value);
      }
    },

    async fetchLocationFromPincode(pincode) {
      this.isFetchingPincodeLocation = true;
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await response.json();

        if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
          const postOffice = data[0].PostOffice[0];
          const state = postOffice.State;
          const district = postOffice.District;

          if (state) {
            // Reset city so fallback can detect "not set"
            this.shipmentForm.customer_city = '';
            this.customerCities = [];
            this.originalCustomerCity = district;
            this.matchAndSetState(state, 'customer');
          }
        }
      } catch (error) {
        console.error('Error fetching location from pincode:', error);
      } finally {
        this.isFetchingPincodeLocation = false;
      }
    },

    async checkIndividualPincodeServiceability(pincode, type) {
      try {
        if (type === 'pickup') {
          this.isCheckingPickupPincode = true;
        } else {
          this.isCheckingCustomerPincode = true;
        }

        const response = await axios.post('/delhivery/check-pincode', {
          pincode: String(pincode)
        });

        if (type === 'pickup') {
          this.isCheckingPickupPincode = false;
          this.pickupPincodeServiceable = response.data.success;
        } else {
          this.isCheckingCustomerPincode = false;
          this.customerPincodeServiceable = response.data.success;
        }

        if (!response.data.success) {
          console.warn(`Pincode ${pincode} is not serviceable`);
        }
      } catch (error) {
        if (type === 'pickup') {
          this.isCheckingPickupPincode = false;
          this.pickupPincodeServiceable = false;
        } else {
          this.isCheckingCustomerPincode = false;
          this.customerPincodeServiceable = false;
        }

        console.error('Error checking pincode serviceability:', error);

        // Show error message to user if it's an API configuration issue
        if (error.response && error.response.data) {
          let errorMessage = error.response.data.error || error.response.data.message;

          // Show toast only for critical errors like API key issues
          if (errorMessage && (errorMessage.includes('API Key') || errorMessage.includes('Login'))) {
            this.$toast.warning(`Pincode check failed: ${errorMessage}. Please contact administrator.`);
          }
        }
      }
    },

    getPickupPincodeHint() {
      if (this.isCheckingPickupPincode) {
        return 'Checking serviceability...';
      } else if (this.pickupPincodeServiceable === true) {
        return 'Pincode is serviceable ✓';
      } else if (this.pickupPincodeServiceable === false) {
        return 'Pincode is not serviceable ✗';
      } else {
        return 'Enter 6-digit pincode for automatic serviceability check';
      }
    },

    getCustomerPincodeHint() {
      if (this.customerAddressType === 'customer') {
        if (this.isCheckingCustomerPincode) {
          return 'Checking serviceability...';
        } else if (this.customerPincodeServiceable === true) {
          return 'Auto-filled from customer - Pincode is serviceable ✓';
        } else if (this.customerPincodeServiceable === false) {
          return 'Auto-filled from customer - Pincode is not serviceable ✗';
        } else {
          return 'Auto-filled from customer information';
        }
      } else if (this.customerAddressType === 'store') {
        if (this.isCheckingCustomerPincode) {
          return 'Checking serviceability...';
        } else if (this.customerPincodeServiceable === true) {
          return 'Auto-filled from store - Pincode is serviceable ✓';
        } else if (this.customerPincodeServiceable === false) {
          return 'Auto-filled from store - Pincode is not serviceable ✗';
        } else {
          return 'Auto-filled from selected store';
        }
      } else {
        if (this.isCheckingCustomerPincode) {
          return 'Checking serviceability...';
        } else if (this.customerPincodeServiceable === true) {
          return 'Pincode is serviceable ✓';
        } else if (this.customerPincodeServiceable === false) {
          return 'Pincode is not serviceable ✗';
        } else {
          return 'Enter 6-digit pincode for automatic serviceability check';
        }
      }
    },

    getFieldHint() {
      if (this.customerAddressType === 'customer') {
        return 'Auto-filled from customer information';
      } else if (this.customerAddressType === 'store') {
        return 'Auto-filled from selected store';
      }
      return '';
    },

    onCustomerAddressTypeChange(type) {
      // Clear selections when switching types
      this.selectedCustomerStoreId = null;
      this.customerPincodeServiceable = null;
      this.customerCities = [];

      if (type === 'customer') {
        // Restore original customer data that was loaded from gift order
        this.shipmentForm.customer_address = this.originalCustomerData.address;
        this.shipmentForm.customer_pincode = this.originalCustomerData.pincode;
        this.shipmentForm.customer_country = this.originalCustomerData.country;

        // Re-fetch state/city from pincode (most accurate)
        const pincode = String(this.originalCustomerData.pincode || '').trim();
        if (pincode.length === 6 && /^\d{6}$/.test(pincode)) {
          this.checkIndividualPincodeServiceability(pincode, 'customer');
          this.fetchLocationFromPincode(pincode);
        } else {
          this.shipmentForm.customer_state = this.originalCustomerData.state;
          this.shipmentForm.customer_city = this.originalCustomerData.city;
        }
      } else if (type === 'store') {
        // Clear address data when switching to store selection
        this.shipmentForm.customer_address = '';
        this.shipmentForm.customer_city = '';
        this.shipmentForm.customer_state = '';
        this.shipmentForm.customer_pincode = '';
        this.shipmentForm.customer_country = '';
      } else if (type === 'custom') {
        // Clear address data for manual entry
        this.shipmentForm.customer_address = '';
        this.shipmentForm.customer_city = '';
        this.shipmentForm.customer_state = '';
        this.shipmentForm.customer_pincode = '';
        this.shipmentForm.customer_country = '';
      }
    },

    onCustomerStoreSelected(storeId) {
      if (storeId) {
        // Find the selected store
        const selectedStoreItem = this.stores.find((s) => s.value === storeId);
        if (selectedStoreItem) {
          const store = selectedStoreItem.store;

          // Store original state/city for matching
          this.originalCustomerState = store.state_jurisdiction || "";
          this.originalCustomerCity = store.city || "";

          // Auto-fill customer address information from store
          this.shipmentForm.customer_address = store.new_address || store.address || "";
          this.shipmentForm.customer_pincode = String(store.pincode || "");
          this.shipmentForm.customer_country = "India";

          // Match state and city with API data
          if (this.indianStates.length > 0) {
            this.matchAndSetState(this.originalCustomerState, 'customer');
          } else {
            // States not loaded yet, will match when loaded
            this.shipmentForm.customer_state = store.state_jurisdiction || "";
            this.shipmentForm.customer_city = store.city || "";
          }

          // Automatically check customer pincode serviceability if pincode is available
          const pincodeStr = String(store.pincode || "");
          if (pincodeStr && pincodeStr.length === 6 && /^\d{6}$/.test(pincodeStr)) {
            this.checkIndividualPincodeServiceability(pincodeStr, 'customer');
          }
        }
      } else {
        // Clear customer address information when no store is selected
        this.shipmentForm.customer_address = "";
        this.shipmentForm.customer_city = "";
        this.shipmentForm.customer_state = "";
        this.shipmentForm.customer_pincode = "";
        this.shipmentForm.customer_country = "";
        this.customerCities = [];
        this.customerPincodeServiceable = null;
      }
    },
  },
};
</script>

<style scoped>
.v-card-actions {
  border-top: 1px solid #e0e0e0;
}
</style>